import {SCENE_TEMPLATES, type SceneTemplateKey, type SponsorOptions, type TakeoverTemplateKey} from "sponsor/sponsorBooking";
import {
	draftSponsorOptions, sponsorOptionsForWire, validateSponsorOptions, type PageThemeTemplateKey,
} from "sponsor/sponsorOptions";
import {composeMessage} from "../composeMessage";
import type {PersonalizationFontKey} from "./personalizationFont";
import {templateImageArity, type PersonalizationAccess, type PersonalizationOffer} from "./personalizationOffer";

/**
 * The per-transfer personalization the sender is composing, and the rules that decide whether it may be
 * sent (kitecorp/transferify-ui#556, api kitecorp/transferify-api-service#461).
 *
 * Plain TypeScript — no React import — because every rule here outlives the sheet that renders it: the
 * arity check is the api's own (`PersonalizationOffer.templates`), the copy bounds are the api's column
 * widths, and the inherited/overridden distinction is the whole product idea of this feature. A rule
 * living in a `useMemo` is a rule that gets rewritten by hand the next time the sheet is restyled, and
 * this repo has no UI-component tests to notice when the rewrite changes it.
 *
 * The draft is a SNAPSHOT layered over the sender's account-level Personalization: it takes effect for
 * one transfer and expires with it, so nothing here writes back to the account.
 */

/** An image or logo already uploaded to the personalization asset endpoints. */
export interface PersonalizationAssetRef {
	assetId: string;
	url: string;
}

/**
 * The account-level values this transfer inherits when the sender overrides nothing.
 *
 * Read from the account brand (`logoUrl`, `accentColor`) and the account page theme (`template`,
 * `imageUrls`). Absent fields mean the account has no default, in which case a per-transfer value is a
 * NEW choice rather than an override — a distinction the sheet shows, and the reason this is a separate
 * type rather than a partial draft.
 */
export interface PersonalizationInherited {
	logoUrl: string | null;
	accentColor: string | null;
	/**
	 * Whatever the ACCOUNT theme is set to, which since kitecorp/transferify-ui#1869 may be a wallpaper —
	 * wider than {@link PersonalizationDraftState.template}, and deliberately so.
	 *
	 * This field is what the sheet compares a draft against to label it inherited/overridden, so it has
	 * to be able to hold a treatment the per-transfer surface does not OFFER. That set narrowed to one
	 * name with kitecorp/transferify-api-service#1149 — a transfer now sells the wallpapers too and
	 * refuses only the VIDEO treatment — but it is not empty, and narrowing this to the offered set would
	 * call the sender's own account theme "no default" the day they choose the one look a transfer
	 * cannot carry.
	 */
	template: TakeoverTemplateKey | null;
	imageUrls: readonly string[];
}

/** The empty inheritance — an account that has personalized nothing. */
export const NO_INHERITED_PERSONALIZATION: PersonalizationInherited = {
	logoUrl: null,
	accentColor: null,
	template: null,
	imageUrls: [],
};

/**
 * What a transfer would look like if this sender personalized nothing.
 *
 * Takes the two account records STRUCTURALLY rather than by their `AccountBrandApi` / `AccountThemeApi`
 * types, so the rule stays a pure function of two shapes instead of dragging two service modules into
 * every test that wants to check an "inherited" label.
 *
 * A DISABLED page theme inherits nothing: recipients do not see it, so calling it inherited would put
 * "inherited" beside a backdrop nobody would have got. The theme's images are narrowed to the ones the
 * SAVED theme actually attaches — the library also holds images the sender uploaded and never published.
 */
export function toInheritedPersonalization(
	brand: {logoUrl?: string | null; primaryColor?: string | null} | null | undefined,
	theme: {
		enabled: boolean;
		template: TakeoverTemplateKey | null;
		images: readonly {url: string; attached: boolean}[];
	} | null | undefined,
): PersonalizationInherited {
	const themed = theme != null && theme.enabled && theme.template !== null;
	return {
		logoUrl: brand?.logoUrl ?? null,
		accentColor: brand?.primaryColor ?? null,
		template: themed ? theme!.template : null,
		imageUrls: themed ? theme!.images.filter(image => image.attached).map(image => image.url) : [],
	};
}

/** The sender's in-progress choices. */
export interface PersonalizationDraftState {
	/** Whether this transfer is personalized at all. `false` sends no draft and charges nothing. */
	enabled: boolean;
	/**
	 * The chosen treatment, or `null` for none — which is what a TEXT-ONLY personalization is (ui#1814,
	 * api#1099). It was non-nullable while every draft had to be paid for: the api's `@NotNull` meant
	 * "required template", and since every treatment needs at least one image that also meant "required
	 * imagery", so a sender who only wanted to sign their transfer had to buy art first.
	 *
	 * A BACKDROP or a WALLPAPER since kitecorp/transferify-api-service#1149 — a transfer sells both now.
	 * Narrowed to what this bundle can DRAW rather than to the whole contract: the picker is filtered by
	 * the offer on top of that, so what a sender may choose is the api's own catalogue intersected with
	 * the treatments that have a renderer here.
	 */
	template: PageThemeTemplateKey | null;
	/**
	 * The chosen wallpaper's per-treatment settings (kitecorp/transferify-api-service#1149).
	 *
	 * ALWAYS an object, never null, and that is what an editing state is: the sender may set a tone,
	 * change their mind about the look, and come back to find the tone still there. What reaches the wire
	 * is `sponsorOptionsForWire`'s answer, which drops every key the chosen look does not take and
	 * collapses "nothing set" to absent — the same normalization the api's `BackdropOptionsValidator`
	 * performs, borrowed rather than restated.
	 *
	 * The very same type, table and validator the sponsor booking form uses (kitecorp/transferify-ui
	 * #1868). A second grammar here would be a setting a sponsor can buy that a sender cannot choose, or
	 * a value one side stores and the other refuses.
	 */
	options: SponsorOptions;
	displayName: string;
	/**
	 * The note's optional heading (ui#1814). It lived in the transfer options sheet until the owner
	 * moved the free copy into the builder on 2026-09-01; there is no separate field for it on the wire,
	 * because neither the transfer nor its personalization has ever had one — {@link ../composeMessage}
	 * folds it into {@link message} with a blank line, and the download page splits it back out.
	 */
	title: string;
	message: string;
	logo: PersonalizationAssetRef | null;
	accentColor: string | null;
	/** Chosen images in RENDER order; the api reads the order, not just the set. */
	images: readonly PersonalizationAssetRef[];
	/** The chosen display face, or `null` for the app's standard one. Branding, so it is paid for. */
	font: PersonalizationFontKey | null;
}

/**
 * What one draft owes, and therefore whether finishing the send costs anything (ui#1814, owner decision
 * 2026-09-01). The ui's half of the api's `PersonalizationFunding`.
 *
 * - `empty` — nothing composed. No personalization is sent and no offer is made.
 * - `free` — the sender's NAME and/or their NOTE, and nothing else. Free on every plan, which is the
 *   whole point of the preview-first flow: a FREE sender who signs their transfer must see their own
 *   name on the recipient's page without paying.
 * - `branding` — a backdrop, a font, a logo, an accent colour or imagery. Included on the tiers that
 *   cover personalization and BOUGHT by the tiers that do not, at send time.
 *
 * Stated once, here, rather than as a condition written at each surface that needs it: the send-time
 * offer, the composer's paywall and the completion payload all ask this one question, and three copies
 * of "which fields cost money" would give a paid feature away the first time one of them fell behind.
 */
export type PersonalizationRung = "empty" | "free" | "branding";

/** Whether this draft names any BRANDING at all — the paid half of {@link PersonalizationRung}. */
function hasBranding(draft: PersonalizationDraftState): boolean {
	// `options` is deliberately NOT asked about. It is only ever meaningful ALONGSIDE a template, which
	// already answers true here, and it is dropped from the wire without one — so counting it would let a
	// tone left over from a look the sender abandoned charge them for a page it cannot appear on. The api
	// reaches the same verdict from the other side: its OPTIONS rung is BRANDING, but a draft carrying
	// options and no template is refused rather than billed.
	return draft.template !== null
		|| draft.font !== null
		|| draft.logo !== null
		|| draft.accentColor !== null
		|| draft.images.length > 0;
}

/** Whether this draft names any free COPY — the sender's own name, or their note's heading or body. */
function hasFreeCopy(draft: PersonalizationDraftState): boolean {
	return draft.displayName.trim() !== "" || composeMessage(draft.title, draft.message) !== "";
}

/**
 * The note as it travels: the heading folded into the body, or `""` when the sender wrote neither.
 *
 * The ONE place the fold happens on this side. The same string is carried twice on purpose — on
 * `PersonalizationDraft.message`, which is what the branded download page renders, and on the
 * transfer's own `message`, which is what the delivery email carries — so the two can never disagree.
 */
export function personalizationNote(draft: PersonalizationDraftState): string {
	return composeMessage(draft.title, draft.message);
}

/** What {@code draft} asks for, independent of what it would cost this particular sender. */
export function personalizationDraftRung(draft: PersonalizationDraftState): PersonalizationRung {
	if (hasBranding(draft)) {
		return "branding";
	}
	return hasFreeCopy(draft) ? "free" : "empty";
}

/**
 * Copy bounds, mirroring the api's `PersonalizationCopyLimits` — which are in turn the column widths in
 * `V35_14_0__add_transfer_personalization.sql`, and the `maxLength` the spec publishes. Held to those
 * values by literals on both sides, exactly like `SPONSOR_LIMITS`.
 */
export const PERSONALIZATION_LIMITS = {
	displayName: 80,
	message: 500,
} as const;

/**
 * The wire shape of `PersonalizationDraft`, as the creation requests AND the upload completion carry it.
 *
 * The completion is the new home since api#1099: the preview-first composer builds the personalization
 * WHILE the bytes upload, so the finished draft does not exist when the init runs.
 */
export interface PersonalizationDraftRequest {
	/**
	 * Absent for a text-only personalization (api#1099). Sending images without one is refused as
	 * contradictory rather than stored and ignored — see {@link PersonalizationDraftError}.
	 */
	template?: PageThemeTemplateKey;
	/**
	 * The wallpaper's settings (kitecorp/transferify-api-service#1149), or ABSENT.
	 *
	 * Absent rather than `null` for a backdrop, matching every other optional field on this request: the
	 * api normalizes an absent object, an empty one and a blank-only one to the same stored NULL, so a
	 * present-but-empty key would be a second spelling of one intent — the exact thing this module's
	 * empty-string rule exists to prevent.
	 */
	options?: SponsorOptions;
	/** Absent means the standard face. There is no `DEFAULT` constant on either side of this contract. */
	font?: PersonalizationFontKey;
	displayName?: string;
	message?: string;
	logoAssetId?: string;
	accentColor?: string;
	imageAssetIds?: string[];
}

/**
 * A look a transfer is ACTUALLY carrying, with the rung it goes out at.
 *
 * The rung travels WITH the request rather than beside it, and rather than being re-derived from the
 * wire shape later (ui#2411). Two reasons, both about one fact having one carrier:
 *
 * - the rung of what is GOING OUT is not always the rung of what was composed: a draft the sender
 *   dismissed carries nothing (`outgoingPersonalization.outgoingPersonalizationRung`), so the only moment
 *   that knows it is the moment the payload is built;
 * - "which fields cost money" is stated once, in {@link personalizationDraftRung}. Reading it back off
 *   {@link PersonalizationDraftRequest} would be a second copy of that rule over the wire spelling of
 *   the same fields, and the first paid facet added to one and not the other is given away free.
 */
export interface AttachedPersonalization {
	request: PersonalizationDraftRequest;
	rung: PersonalizationRung;
}

/**
 * A fresh, empty draft.
 *
 * Deliberately starts DISABLED and inherits nothing: opening the builder must not silently attach a
 * personalization to a transfer, and pre-filling the account's own theme would make "inherited"
 * indistinguishable from "the sender chose exactly this".
 *
 * It also starts with NO backdrop, which is what changed with ui#1814. It used to be seeded with the
 * first catalogue template, because a template was mandatory; that seeding now would put every draft on
 * the BRANDING rung — and therefore behind a payment — before the sender had chosen anything at all.
 */
export function emptyPersonalizationDraft(): PersonalizationDraftState {
	return {
		enabled: false,
		template: null,
		// Black words on a white background (kitecorp/transferify-ui#2416). Not branding on its own: the
		// options never reach the wire without a template, and `hasBranding` does not read them.
		options: draftSponsorOptions(null),
		displayName: "",
		title: "",
		message: "",
		logo: null,
		accentColor: null,
		images: [],
		font: null,
	};
}

/**
 * A look the sender did not pick in the builder but ARRIVED with (kitecorp/transferify-ui#3776): the landing's
 * Halloween slide, pressed, or a `?tema=` link puts the slide's scene on the draft, armed, so the next send
 * carries it without the builder ever opening. The imagery of whatever look it replaces goes, as `setTemplate`
 * drops it when the backdrop goes: a scene composes none, and images with nothing to render them into are
 * refused. Everything else composed (the name, the note, a logo, a colour) stays, because the scene is a look
 * added to the sender's transfer, not a builder reset.
 */
export function preselectPersonalizationTemplate(
	draft: PersonalizationDraftState,
	template: PageThemeTemplateKey,
): PersonalizationDraftState {
	return {...draft, enabled: true, template, images: []};
}

/**
 * The off switch beside the Personalise opener (kitecorp/transferify-ui#3776): the look goes, its imagery with
 * it, and the draft stays armed only while something composed remains. A draft left with nothing is DISARMED
 * rather than left armed and empty: an armed empty draft sends nothing anyway, but the opener would keep its
 * tick over a transfer that goes out plain. A draft the sender had dismissed stays dismissed.
 */
export function clearPersonalizationTemplate(draft: PersonalizationDraftState): PersonalizationDraftState {
	const cleared = {...draft, template: null, images: []};
	return {...cleared, enabled: draft.enabled && personalizationDraftRung(cleared) !== "empty"};
}

/**
 * The scene a draft carries (kitecorp/transferify-ui#3776), which the composer shows on a row of its own with the
 * off switch, because it arrived from a Halloween slide or a `?tema=` link. A backdrop or a wallpaper picked in the
 * builder stays the opener's tick, as before.
 */
export function draftSceneTemplate(draft: PersonalizationDraftState): SceneTemplateKey | null {
	return SCENE_TEMPLATES.find(scene => scene === draft.template) ?? null;
}

/** Why a draft cannot be sent yet. `null` means it can. */
export type PersonalizationDraftError =
	| "templateUnavailable"
	| "imagesWithoutTemplate"
	| "optionsInvalid"
	| "tooFewImages"
	| "tooManyImages"
	| "displayNameTooLong"
	| "messageTooLong";

/**
 * The first reason this draft would be refused, or `null`.
 *
 * Arity is checked against the OFFER rather than against a local table: the api publishes each
 * template's minimum and maximum precisely so the ui enforces the same rule it does. A local copy would
 * be a second source of truth whose only symptom, when it drifts, is a 400 the sender cannot act on.
 *
 * A rule about the draft's CONTENT, deliberately blind to {@link PersonalizationDraftState.enabled}.
 * It used to short-circuit on a disabled draft ("nothing to refuse"), which was right while the enable
 * toggle existed: disabled meant "not being composed". Since ui#1409 removed that toggle, `enabled`
 * means "the sender committed this", and it is only set when they leave the composer — so the
 * short-circuit silenced the over-long-name and unavailable-animation lines for the entire time the
 * sender was actually composing, which is the only time they can act on them. The two callers that
 * genuinely need "and it is committed" — {@link isPersonalizationSendable}'s consumer
 * {@link toPersonalizationRequest}, and the view-model's `blocksSend` — already say so themselves, so
 * the condition was a third copy of a rule that lives better in one place.
 */
export function personalizationDraftError(
	draft: PersonalizationDraftState,
	offer: PersonalizationOffer | null,
): PersonalizationDraftError | null {
	if (draft.displayName.length > PERSONALIZATION_LIMITS.displayName) {
		return "displayNameTooLong";
	}
	// The COMPOSED note, because that is the string the column stores: bounding the body alone would
	// admit a heading that pushes the pair past the api's width and turn a localized 400 into a refusal
	// the sender cannot see the cause of.
	if (personalizationNote(draft).length > PERSONALIZATION_LIMITS.message) {
		return "messageTooLong";
	}
	if (draft.template === null) {
		// No backdrop is a legitimate draft since api#1099 — it is what a text-only personalization is —
		// but images with nothing to render them into are refused rather than stored and ignored, which is
		// the api's own `PERSONALIZATION_IMAGES_WITHOUT_TEMPLATE`. Stating it here rather than letting the
		// api answer keeps the sender's fix (choose an animation, or drop the pictures) in front of them.
		return draft.images.length > 0 ? "imagesWithoutTemplate" : null;
	}
	const arity = templateImageArity(offer, draft.template);
	if (arity === null) {
		return "templateUnavailable";
	}
	// The api's own verdict, from the api's own grammar: `validateSponsorOptions` is the module the
	// booking form and the option controls both read, so a value this builder accepts is one the server
	// accepts. Judged on what would actually be SENT, not on the editing state — a tone left over from a
	// look the sender moved away from is dropped on the wire and must not block the send.
	if (Object.keys(validateSponsorOptions(draft.template, personalizationOptionsForWire(draft) ?? {})).length > 0) {
		return "optionsInvalid";
	}
	if (draft.images.length < arity.minimumImages) {
		return "tooFewImages";
	}
	if (draft.images.length > arity.maximumImages) {
		return "tooManyImages";
	}
	return null;
}

/**
 * Whether an armed draft holds the send back (ui#763), judged only where a judgement is possible.
 *
 * A signed-out visitor has no offer to judge against BY DESIGN (the catalogue is published to accounts only), so
 * every template reads as unavailable to them; their press goes to the sign-in, and the press after it is made
 * against the offer their account is given (`advanceTransferCreation` returns to the form after a confirmation).
 * Holding that first press would stop a visitor who arrived for the Halloween slide's look
 * (kitecorp/transferify-ui#3776) at a line about picking another animation, which is not what they need to do.
 */
export function personalizationBlocksSend(
	access: PersonalizationAccess, enabled: boolean, error: PersonalizationDraftError | null,
): boolean {
	return access === "composable" && enabled && error !== null;
}

/** True when this draft may ride along on a transfer creation. */
export function isPersonalizationSendable(
	draft: PersonalizationDraftState,
	offer: PersonalizationOffer | null,
): boolean {
	return personalizationDraftError(draft, offer) === null;
}

/**
 * The draft as the creation request carries it, or `null` when this transfer is not personalized.
 *
 * Empty strings become ABSENT rather than `""`: the api treats an empty display name as "no name" and a
 * present-but-empty field would be a second spelling of the same intent, which is how a download page
 * ends up rendering a blank heading where the account name belonged. An unsendable draft returns null
 * for the same reason a disabled one does — the request must never carry something the api will refuse.
 *
 * An `empty` rung returns null too: since api#1099 a draft with no content at all creates no row, so
 * sending one would be a round trip that asks the api to do nothing.
 */
export function toPersonalizationRequest(
	draft: PersonalizationDraftState,
	offer: PersonalizationOffer | null,
): PersonalizationDraftRequest | null {
	if (!draft.enabled || !isPersonalizationSendable(draft, offer)
		|| personalizationDraftRung(draft) === "empty") {
		return null;
	}
	const displayName = draft.displayName.trim();
	const message = personalizationNote(draft);
	const options = personalizationOptionsForWire(draft);
	return {
		...(draft.template !== null ? {template: draft.template} : {}),
		...(options !== null ? {options} : {}),
		...(draft.font !== null ? {font: draft.font} : {}),
		...(displayName !== "" ? {displayName} : {}),
		...(message !== "" ? {message} : {}),
		...(draft.logo !== null ? {logoAssetId: draft.logo.assetId} : {}),
		...(draft.accentColor !== null ? {accentColor: draft.accentColor} : {}),
		...(draft.images.length > 0 ? {imageAssetIds: draft.images.map(image => image.assetId)} : {}),
	};
}

/**
 * The settings this draft would actually SUBMIT, or `null` when it would submit none.
 *
 * With no template there is nothing to configure, so nothing is sent — the api refuses options without
 * one rather than storing them, and this is the client half of that. With one, every key the chosen look
 * does not take is dropped rather than refused, exactly as the sponsor form does: a sender who lays out a
 * wipe and then picks a crossfade changed their mind, they did not make a mistake.
 *
 * EXPORTED so the view model validates the very object this sends. Judged on the raw editing state
 * instead, the same validator answers differently — a leftover `direction` under a `CROSSFADE` is
 * "not allowed" there and absent here — and the send block and the message under a control would then
 * disagree about one draft.
 */
export function personalizationOptionsForWire(
	draft: PersonalizationDraftState,
): SponsorOptions | null {
	return draft.template === null ? null : sponsorOptionsForWire(draft.template, draft.options);
}

/** Which personalization facets can differ from the account's own. */
export type PersonalizationField = "template" | "logo" | "accentColor" | "images";

/**
 * Where a field's value comes from, for the sheet to say so.
 *
 * Three states, not two: `inherited` (the account already sets this and the transfer keeps it),
 * `overridden` (the account sets it and this transfer differs), and `added` (the account sets nothing,
 * so this is a new choice rather than an override). Collapsing `added` into `overridden` would put
 * "overridden" beside a value that overrides nothing, which is the one thing this label exists to say.
 */
export type PersonalizationFieldOrigin = "inherited" | "overridden" | "added" | "unset";

export function personalizationFieldOrigin(
	field: PersonalizationField,
	draft: PersonalizationDraftState,
	inherited: PersonalizationInherited,
): PersonalizationFieldOrigin {
	switch (field) {
		case "template":
			return originOf(draft.template, inherited.template);
		case "logo":
			return originOf(draft.logo?.url ?? null, inherited.logoUrl);
		case "accentColor":
			return originOf(draft.accentColor, inherited.accentColor);
		case "images":
			return originOfList(draft.images.map(image => image.url), inherited.imageUrls);
	}
}

function originOf(chosen: string | null, inheritedValue: string | null): PersonalizationFieldOrigin {
	if (chosen === null) {
		return inheritedValue === null ? "unset" : "inherited";
	}
	if (inheritedValue === null) {
		return "added";
	}
	return chosen === inheritedValue ? "inherited" : "overridden";
}

function originOfList(chosen: readonly string[], inheritedValue: readonly string[]): PersonalizationFieldOrigin {
	if (chosen.length === 0) {
		return inheritedValue.length === 0 ? "unset" : "inherited";
	}
	if (inheritedValue.length === 0) {
		return "added";
	}
	const same = chosen.length === inheritedValue.length
		&& chosen.every((url, index) => url === inheritedValue[index]);
	return same ? "inherited" : "overridden";
}

/** Adds an image to the end of the render order, ignoring one already chosen. */
export function addPersonalizationImage(
	images: readonly PersonalizationAssetRef[],
	image: PersonalizationAssetRef,
): readonly PersonalizationAssetRef[] {
	return images.some(chosen => chosen.assetId === image.assetId) ? images : [...images, image];
}

/** Drops an image from the render order. */
export function removePersonalizationImage(
	images: readonly PersonalizationAssetRef[],
	assetId: string,
): readonly PersonalizationAssetRef[] {
	return images.filter(image => image.assetId !== assetId);
}

// Manual reordering (movePersonalizationImage) was removed with the composer's left/right controls
// (ui#779, owner decision — same rule ui#686 set on the personalize page): the animation picks and
// rotates images itself, so order is pick order and not the sender's editorial concern.
